import {
    createDeviceKeyValueStorage,
    type DeviceKeyValueStorage,
} from '~~/layers/base/utils/deviceKeyValueStorage'
import {
    isAccountAuthSessionKey,
    type AuthHydrateSessionKey,
} from '~~/layers/base/utils/authSessionKey'

/**
 * Device storage — must survive app relaunch, not just refresh. The Send flow reads this on the
 * first frame to avoid opening the stationery accordion over a hero the user already has.
 */
const HAS_CUSTOM_STATIONERY_STORAGE_PREFIX = 'crane.hasCustomStationery.v1'

/** Created on first use, then reused. See {@link deviceStorage}. */
let storage: DeviceKeyValueStorage | null = null

/**
 * Last account key this tab persisted a verdict for. Logout can run *after* the auth swap
 * (`clearSessionAfterAuthSwap`), leaving no resolvable key to address the entry — this lets the
 * clear still find it.
 */
let lastWrittenSessionKey: AuthHydrateSessionKey = null

/**
 * The shared storage backend, built on the first call and cached for later ones.
 *
 * Built lazily rather than at module load because the backend it picks depends on the platform and
 * on `window` being present, neither of which is settled while this module is first imported.
 */
function deviceStorage(): DeviceKeyValueStorage {
    if (storage === null) {
        storage = createDeviceKeyValueStorage()
    }
    return storage
}

/**
 * Per-account storage key, or `null` when the session cannot own a cached verdict (pending auth,
 * no user id, or an anonymous guest). Scoping by account id keeps one user's verdict from seeding
 * another's first frame on a shared device.
 */
function storageKeyFor(sessionKey: AuthHydrateSessionKey): string | null {
    if (!isAccountAuthSessionKey(sessionKey)) {
        return null
    }
    return `${HAS_CUSTOM_STATIONERY_STORAGE_PREFIX}:${sessionKey}`
}

/**
 * Cached "does this account have active custom stationery" verdict.
 *
 * `null` means unknown (never resolved for this account, or unreadable storage) — callers must
 * treat that as a distinct third state rather than coercing it to `false`.
 */
export async function readHasCustomStationeryCache(
    sessionKey: AuthHydrateSessionKey
): Promise<boolean | null> {
    const key = storageKeyFor(sessionKey)
    if (key == null) {
        return null
    }
    try {
        const raw = await deviceStorage().getItem(key)
        if (raw !== 'true' && raw !== 'false') {
            return null
        }
        return raw === 'true'
    } catch {
        return null
    }
}

/** Persists a resolved verdict so the next cold start can render the accordion without a fetch. */
export async function writeHasCustomStationeryCache(
    sessionKey: AuthHydrateSessionKey,
    hasCustomStationery: boolean
): Promise<void> {
    const key = storageKeyFor(sessionKey)
    if (key == null) {
        return
    }
    try {
        await deviceStorage().setItem(key, hasCustomStationery ? 'true' : 'false')
        lastWrittenSessionKey = sessionKey
    } catch {
        /* Best-effort — a missing cache only costs one open-by-default paint */
    }
}

/**
 * Drops the cached verdict on explicit sign-out. Not called on guest context flips, which are a
 * normal cold-start state rather than a sign-out.
 *
 * Falls back to {@link lastWrittenSessionKey} when the passed key no longer resolves, so a clear
 * that runs after the auth swap still removes the departing account's entry.
 */
export async function clearHasCustomStationeryCache(
    sessionKey: AuthHydrateSessionKey
): Promise<void> {
    const key = storageKeyFor(sessionKey) ?? storageKeyFor(lastWrittenSessionKey)
    if (key == null) {
        return
    }
    lastWrittenSessionKey = null
    try {
        await deviceStorage().removeItem(key)
    } catch {
        /* Best-effort */
    }
}
